import type { ComponentInstance } from "building-types/components/componentInstanceTypes";
import type { Doc } from "building-types/doc";
import type { ComponentHandle } from "building-types/handles/docEntityHandleTypes";
import type { Table } from "common/data/table";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { docEntityGetComponent } from "./entity/docGetEntityComponent";

const EMPTY_COMPONENT_INSTANCES: readonly ComponentInstance[] = Object.freeze([]);

/**
 * Secondary index: componentId → instances of that component.
 * Cached by `doc.componentInstances` table identity (same pattern as componentAssociations).
 */
const componentInstancesByComponentIdCache = new WeakMap<
  Table<ComponentInstance>,
  ReadonlyMap<string, readonly ComponentInstance[]>
>();

function getComponentInstancesByComponentId(
  doc: Pick<Doc, "componentInstances">,
): ReadonlyMap<string, readonly ComponentInstance[]> {
  const table = doc.componentInstances;
  const cached = componentInstancesByComponentIdCache.get(table);
  if (cached) {
    return cached;
  }

  const byComponentId = new Map<string, ComponentInstance[]>();
  for (const id in table) {
    const instance = table[id];
    let instances = byComponentId.get(instance.componentId);
    if (!instances) {
      instances = [];
      byComponentId.set(instance.componentId, instances);
    }
    instances.push(instance);
  }

  for (const [componentId, instances] of byComponentId) {
    byComponentId.set(componentId, Object.freeze(sortEntitiesBySortIndex(instances)) as ComponentInstance[]);
  }

  componentInstancesByComponentIdCache.set(table, byComponentId);
  return byComponentId;
}

/** Gets all component instances that belong to a component. */
export function docGetComponentInstances(doc: Doc, handle: ComponentHandle): readonly ComponentInstance[] {
  return getComponentInstancesByComponentId(doc).get(handle.id) ?? EMPTY_COMPONENT_INSTANCES;
}

/**
 * Per-doc cache of recursive parent component ids.
 * Keyed on Doc identity — safe for the duration of a doc-ref delta where `doc` is stable.
 */
const recursiveParentComponentsCache = new WeakMap<Doc, Map<string, readonly string[]>>();

function computeRecursiveParentComponents(doc: Doc, componentId: string, visitedComponents: Set<string>): string[] {
  if (visitedComponents.has(componentId)) {
    return [];
  }
  visitedComponents.add(componentId);

  const parentComponentIds = new Set<string>();
  const componentInstances = docGetComponentInstances(doc, { type: "COMPONENT", id: componentId });

  for (const instance of componentInstances) {
    const parentComponent = docEntityGetComponent(doc, { type: "COMPONENT_INSTANCE", id: instance.id });
    if (parentComponent) {
      parentComponentIds.add(parentComponent.id);
      const ancestors = computeRecursiveParentComponents(doc, parentComponent.id, visitedComponents);
      for (const ancestorId of ancestors) {
        parentComponentIds.add(ancestorId);
      }
    }
  }

  return Array.from(parentComponentIds);
}

/**
 * Recursively finds all parent components that instantiate the given component.
 */
export function docGetComponentRecursiveParentComponents(
  doc: Doc,
  componentId: string,
  visitedComponents: Set<string> = new Set(),
): readonly string[] {
  // Only cache complete top-level results. Recursive calls pass a non-empty visited set
  // for cycle detection and must not read/write the cache mid-computation.
  const isTopLevel = visitedComponents.size === 0;
  if (isTopLevel) {
    let perDoc = recursiveParentComponentsCache.get(doc);
    if (!perDoc) {
      perDoc = new Map();
      recursiveParentComponentsCache.set(doc, perDoc);
    }
    const cached = perDoc.get(componentId);
    if (cached) {
      return cached;
    }

    const result = Object.freeze(computeRecursiveParentComponents(doc, componentId, visitedComponents));
    perDoc.set(componentId, result);
    return result;
  }

  return computeRecursiveParentComponents(doc, componentId, visitedComponents);
}
